import { FormEvent, ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { DraggableDetailPanel } from '../../components/ui/DraggableDetailPanel';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { FormActionBar } from '../../components/ui/FormActionBar';
import {
  PortalRequestItem,
  PortalRequestPriority,
  PortalRequestStatus,
  PortalRequestType,
  portalRequestsMock,
} from './portalRequestsMock';

type DetailTab = 'details' | 'comments' | 'attachments' | 'history';

const typeLabel: Record<PortalRequestType, string> = {
  improvement: 'Melhoria',
  change: 'Mudança',
  question: 'Dúvida',
  support: 'Suporte',
};

const typeClass: Record<PortalRequestType, string> = {
  improvement: 'bg-violet-50 text-violet-700',
  change: 'bg-primary-50 text-primary-700',
  question: 'bg-emerald-50 text-emerald-700',
  support: 'bg-orange-50 text-orange-700',
};

const priorityLabel: Record<PortalRequestPriority, string> = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

const priorityClass: Record<PortalRequestPriority, string> = {
  high: 'bg-red-50 text-red-700',
  medium: 'bg-amber-50 text-amber-700',
  low: 'bg-emerald-50 text-emerald-700',
};

const statusLabel: Record<PortalRequestStatus, string> = {
  awaitingApproval: 'Aguardando sua aprovação',
  inProgress: 'Em andamento',
  analysis: 'Em análise',
  answered: 'Respondida',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

const statusClass: Record<PortalRequestStatus, string> = {
  awaitingApproval: 'bg-violet-50 text-violet-700',
  inProgress: 'bg-orange-50 text-orange-700',
  analysis: 'bg-primary-50 text-primary-700',
  answered: 'bg-sky-50 text-sky-700',
  completed: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-red-50 text-red-700',
};

const typeOptions: { id: PortalRequestType; label: string }[] = [
  { id: 'improvement', label: 'Melhoria' },
  { id: 'change', label: 'Mudança' },
  { id: 'question', label: 'Dúvida' },
  { id: 'support', label: 'Suporte' },
];

const priorityOptions: { id: PortalRequestPriority; label: string }[] = [
  { id: 'high', label: 'Alta' },
  { id: 'medium', label: 'Média' },
  { id: 'low', label: 'Baixa' },
];

const pageSize = 8;

type NewRequestForm = {
  title: string;
  description: string;
  type: PortalRequestType;
  priority: PortalRequestPriority;
  projectId: string;
};

const emptyForm = (projectId: string): NewRequestForm => ({
  title: '',
  description: '',
  type: 'improvement',
  priority: 'medium',
  projectId,
});

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export function PortalRequestsPage() {
  const [items, setItems] = useState(portalRequestsMock.items);
  const [query, setQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>('req-015');
  const [detailTab, setDetailTab] = useState<DetailTab>('details');
  const [approvedIds, setApprovedIds] = useState<string[]>([]);
  const [adjustmentSentIds, setAdjustmentSentIds] = useState<string[]>([]);
  const [adjustmentsOpen, setAdjustmentsOpen] = useState(false);
  const [adjustmentMessage, setAdjustmentMessage] = useState('');
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [form, setForm] = useState<NewRequestForm>(emptyForm(portalRequestsMock.projects[0].id));

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR');
    return items.filter((item) => {
      const status = approvedIds.includes(item.id) ? 'completed' : item.status;
      if (projectFilter !== 'all' && item.projectId !== projectFilter) return false;
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (priorityFilter !== 'all' && item.priority !== priorityFilter) return false;
      if (!normalized) return true;
      const haystack = `${item.number} ${item.title} ${item.projectName} ${typeLabel[item.type]}`.toLocaleLowerCase(
        'pt-BR',
      );
      return haystack.includes(normalized);
    });
  }, [approvedIds, items, priorityFilter, projectFilter, query, statusFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const selected = useMemo(() => {
    const item = items.find((entry) => entry.id === selectedId) ?? null;
    if (!item) return null;
    if (approvedIds.includes(item.id)) {
      return { ...item, status: 'completed' as const };
    }
    return item;
  }, [approvedIds, items, selectedId]);

  const summary = useMemo(() => {
    const effective = items.map((item) =>
      approvedIds.includes(item.id) ? ({ ...item, status: 'completed' as const }) : item,
    );
    return {
      total: effective.length,
      inProgress: effective.filter((item) => item.status === 'inProgress' || item.status === 'analysis').length,
      awaitingApproval: effective.filter((item) => item.status === 'awaitingApproval').length,
      completed: effective.filter((item) => item.status === 'completed' || item.status === 'answered').length,
      cancelled: effective.filter((item) => item.status === 'cancelled').length,
    };
  }, [approvedIds, items]);

  function openNewModal() {
    setForm(emptyForm(portalRequestsMock.projects[0].id));
    setNewModalOpen(true);
  }

  function updateForm<K extends keyof NewRequestForm>(key: K, value: NewRequestForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function submitNew(event: FormEvent) {
    event.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    const project =
      portalRequestsMock.projects.find((entry) => entry.id === form.projectId) ?? portalRequestsMock.projects[0];
    const maxNumber = items.reduce((max, item) => {
      const parsed = Number.parseInt(item.number, 10);
      return Number.isFinite(parsed) ? Math.max(max, parsed) : max;
    }, 0);
    const number = String(maxNumber + 1).padStart(3, '0');
    const nowLabel = new Date().toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const next: PortalRequestItem = {
      id: `req-${number}-${Date.now()}`,
      number,
      title: form.title.trim(),
      description: form.description.trim(),
      type: form.type,
      priority: form.priority,
      status: form.type === 'change' || form.type === 'improvement' ? 'analysis' : 'inProgress',
      projectId: project.id,
      projectName: project.name,
      createdAtLabel: nowLabel,
      updatedAtLabel: nowLabel,
      requestedByName: 'Ana Souza',
      requestedByRole: 'Patrocinadora',
      ownerName: 'Rafael Oliveira',
      ownerRole: 'Gerente do projeto',
      impactSchedule: form.type === 'question' || form.type === 'support' ? undefined : 'A avaliar',
      impactCost: form.type === 'question' || form.type === 'support' ? undefined : 'Não estimado',
      comments: [],
      attachments: [],
      links: [],
      history: [
        {
          id: `h-${Date.now()}`,
          title: 'Solicitação aberta',
          detail: 'Registrada por Ana Souza',
          whenLabel: nowLabel,
        },
      ],
    };
    setItems((current) => [next, ...current]);
    setSelectedId(next.id);
    setDetailTab('details');
    setPage(1);
    setNewModalOpen(false);
  }

  function submitAdjustments(event: FormEvent) {
    event.preventDefault();
    if (!selected || !adjustmentMessage.trim()) return;
    setAdjustmentSentIds((current) =>
      current.includes(selected.id) ? current : [...current, selected.id],
    );
    setAdjustmentsOpen(false);
    setAdjustmentMessage('');
  }

  return (
    <div className={`relative space-y-5 ${selected ? 'xl:pr-[24rem]' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Solicitações</h1>
          <p className="mt-1 max-w-2xl text-sm text-secondary-600">
            Acompanhe suas solicitações de suporte, dúvidas, melhorias e mudanças.
          </p>
        </div>
        <button type="button" onClick={openNewModal} className="erp-btn-primary inline-flex items-center gap-1.5">
          <PlusIcon className="h-4 w-4" aria-hidden />
          Nova solicitação
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <SummaryCard label="Total" value={summary.total} tone="blue" />
        <SummaryCard label="Em andamento" value={summary.inProgress} tone="orange" />
        <SummaryCard label="Aguardando sua aprovação" value={summary.awaitingApproval} tone="violet" />
        <SummaryCard label="Concluídas" value={summary.completed} tone="green" />
        <SummaryCard label="Canceladas" value={summary.cancelled} tone="red" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[14rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Buscar solicitações..."
            className="w-full rounded-xl border border-secondary-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          />
        </label>
        <select
          value={projectFilter}
          onChange={(event) => {
            setProjectFilter(event.target.value);
            setPage(1);
          }}
          className="rounded-xl border border-secondary-200 bg-white px-3 py-2 text-sm"
        >
          <option value="all">Projeto</option>
          {portalRequestsMock.projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(event) => {
            setTypeFilter(event.target.value);
            setPage(1);
          }}
          className="rounded-xl border border-secondary-200 bg-white px-3 py-2 text-sm"
        >
          <option value="all">Tipo</option>
          {typeOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(1);
          }}
          className="rounded-xl border border-secondary-200 bg-white px-3 py-2 text-sm"
        >
          <option value="all">Status</option>
          {(Object.keys(statusLabel) as PortalRequestStatus[]).map((status) => (
            <option key={status} value={status}>
              {statusLabel[status]}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(event) => {
            setPriorityFilter(event.target.value);
            setPage(1);
          }}
          className="rounded-xl border border-secondary-200 bg-white px-3 py-2 text-sm"
        >
          <option value="all">Prioridade</option>
          {priorityOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
        <button type="button" className="erp-btn-secondary erp-btn-sm inline-flex items-center gap-1.5">
          <FunnelIcon className="h-4 w-4" aria-hidden />
          Mais filtros
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed text-left text-sm">
            <colgroup>
              <col className="w-10" />
              <col className="w-16" />
              <col className="w-[22%]" />
              <col className="w-28" />
              <col className="w-24" />
              <col className="w-[16%]" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-40" />
              <col className="w-12" />
            </colgroup>
            <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
              <tr>
                <th className="px-3 py-3">
                  <span className="sr-only">Selecionar</span>
                </th>
                <th className="px-3 py-3 font-medium">#</th>
                <th className="px-3 py-3 font-medium">Título</th>
                <th className="px-3 py-3 font-medium">Tipo</th>
                <th className="px-3 py-3 font-medium">Prioridade</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium">Data de criação</th>
                <th className="px-3 py-3 font-medium">Última atualização</th>
                <th className="px-3 py-3 font-medium">Responsável</th>
                <th className="px-3 py-3 font-medium">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => {
                const status = approvedIds.includes(item.id) ? 'completed' : item.status;
                return (
                  <tr
                    key={item.id}
                    onClick={() => {
                      setSelectedId(item.id);
                      setDetailTab('details');
                    }}
                    className={`cursor-pointer border-t border-secondary-100 ${
                      selectedId === item.id ? 'bg-primary-50/70' : 'hover:bg-secondary-50'
                    }`}
                  >
                    <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                      <input type="checkbox" className="rounded border-secondary-300" aria-label={`Selecionar ${item.number}`} />
                    </td>
                    <td className="px-3 py-3 font-medium text-secondary-500">#{item.number}</td>
                    <td className="px-3 py-3">
                      <p className="truncate font-semibold text-secondary-900" title={item.title}>
                        {item.title}
                      </p>
                      <p className="truncate text-xs text-secondary-500">{item.projectName}</p>
                    </td>
                    <td className="px-3 py-3">
                      <Tag className={typeClass[item.type]}>{typeLabel[item.type]}</Tag>
                    </td>
                    <td className="px-3 py-3">
                      <Tag className={priorityClass[item.priority]}>{priorityLabel[item.priority]}</Tag>
                    </td>
                    <td className="px-3 py-3">
                      <Tag className={statusClass[status]}>{statusLabel[status]}</Tag>
                    </td>
                    <td className="truncate px-3 py-3 text-secondary-600">{item.createdAtLabel.split(' ')[0]}</td>
                    <td className="truncate px-3 py-3 text-secondary-600">{item.updatedAtLabel.split(' ')[0]}</td>
                    <td className="px-3 py-3">
                      <span className="inline-flex min-w-0 items-center gap-2">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                          {initials(item.ownerName)}
                        </span>
                        <span className="truncate text-secondary-700">{item.ownerName}</span>
                      </span>
                    </td>
                    <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        className="rounded-lg p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
                        aria-label="Mais opções"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {pageItems.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-secondary-500">Nenhuma solicitação com esse filtro.</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-secondary-100 px-4 py-3 text-sm text-secondary-600">
          <p>
            Mostrando {(currentPage - 1) * pageSize + (pageItems.length > 0 ? 1 : 0)} a{' '}
            {Math.min(currentPage * pageSize, filtered.length)} de {filtered.length} solicitações
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="rounded-lg border border-secondary-200 p-1.5 disabled:opacity-40"
              aria-label="Página anterior"
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden />
            </button>
            <span className="min-w-[4rem] text-center font-medium text-secondary-800">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              className="rounded-lg border border-secondary-200 p-1.5 disabled:opacity-40"
              aria-label="Próxima página"
            >
              <ChevronRightIcon className="h-4 w-4" aria-hidden />
            </button>
            <span className="text-xs text-secondary-500">{pageSize} por página</span>
          </div>
        </div>
      </div>

      {selected ? (
        <RequestDetailPanel
          item={selected}
          activeTab={detailTab}
          onTabChange={setDetailTab}
          onClose={() => setSelectedId(null)}
          adjustmentSent={adjustmentSentIds.includes(selected.id)}
          onApprove={() =>
            setApprovedIds((current) => (current.includes(selected.id) ? current : [...current, selected.id]))
          }
          onRequestAdjustments={() => {
            setAdjustmentMessage('');
            setAdjustmentsOpen(true);
          }}
        />
      ) : null}

      <DraggableModalPanel
        open={newModalOpen}
        title="Nova solicitação"
        subtitle="Registre um pedido de suporte, dúvida, melhoria ou mudança."
        onClose={() => setNewModalOpen(false)}
        footer={
          <FormActionBar showCancel onCancel={() => setNewModalOpen(false)}>
            <button
              type="submit"
              form="portal-new-request-form"
              className="erp-btn-primary"
              disabled={!form.title.trim() || !form.description.trim()}
            >
              Criar solicitação
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-new-request-form" onSubmit={submitNew} className="space-y-4">
          <label className="block">
            <span className="erp-label">Título</span>
            <input
              value={form.title}
              onChange={(event) => updateForm('title', event.target.value)}
              required
              placeholder="Ex.: Incluir novo indicador no dashboard"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
          <label className="block">
            <span className="erp-label">Descrição</span>
            <textarea
              value={form.description}
              onChange={(event) => updateForm('description', event.target.value)}
              required
              rows={4}
              placeholder="Descreva o pedido com o máximo de contexto possível..."
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="erp-label">Tipo</span>
              <select
                value={form.type}
                onChange={(event) => updateForm('type', event.target.value as PortalRequestType)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {typeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Prioridade</span>
              <select
                value={form.priority}
                onChange={(event) => updateForm('priority', event.target.value as PortalRequestPriority)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {priorityOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="erp-label">Projeto</span>
              <select
                value={form.projectId}
                onChange={(event) => updateForm('projectId', event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {portalRequestsMock.projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </form>
      </DraggableModalPanel>

      <DraggableModalPanel
        open={adjustmentsOpen}
        title="Solicitar ajustes"
        subtitle={selected ? `#${selected.number} — ${selected.title}` : undefined}
        onClose={() => setAdjustmentsOpen(false)}
        footer={
          <FormActionBar showCancel onCancel={() => setAdjustmentsOpen(false)}>
            <button
              type="submit"
              form="portal-request-adjustments-form"
              className="erp-btn-primary"
              disabled={!adjustmentMessage.trim()}
            >
              Enviar solicitação
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-request-adjustments-form" onSubmit={submitAdjustments} className="space-y-3">
          <label className="block">
            <span className="erp-label">Mensagem</span>
            <textarea
              value={adjustmentMessage}
              onChange={(event) => setAdjustmentMessage(event.target.value)}
              required
              rows={5}
              placeholder="Descreva os ajustes necessários..."
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
        </form>
      </DraggableModalPanel>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'blue' | 'orange' | 'violet' | 'green' | 'red';
}) {
  const toneClass = {
    blue: 'bg-primary-50 text-primary-700',
    orange: 'bg-orange-50 text-orange-700',
    violet: 'bg-violet-50 text-violet-700',
    green: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-700',
  }[tone];

  return (
    <article className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
      <p className="text-sm text-secondary-500">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${toneClass.split(' ')[1]}`}>{value}</p>
      <span className={`mt-3 inline-flex h-2 w-10 rounded-full ${toneClass.split(' ')[0]}`} />
    </article>
  );
}

function Tag({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className={`inline-flex max-w-full truncate rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}>
      {children}
    </span>
  );
}

function RequestDetailPanel({
  item,
  activeTab,
  onTabChange,
  onClose,
  adjustmentSent,
  onApprove,
  onRequestAdjustments,
}: {
  item: PortalRequestItem;
  activeTab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
  onClose: () => void;
  adjustmentSent: boolean;
  onApprove: () => void;
  onRequestAdjustments: () => void;
}) {
  const canAct = item.status === 'awaitingApproval';

  return (
    <DraggableDetailPanel
      resetKey={item.id}
      header={
        <div className="border-b border-secondary-100 px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-semibold text-secondary-500">#{item.number}</p>
                <Tag className={statusClass[item.status]}>{statusLabel[item.status]}</Tag>
              </div>
              <h2 className="mt-1 text-base font-semibold text-secondary-900">{item.title}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
              aria-label="Fechar detalhes"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
      }
      footer={
        canAct && activeTab === 'details' ? (
          <div className="flex items-center gap-2 p-4">
            <button
              type="button"
              onClick={onApprove}
              className="erp-btn-primary flex flex-1 items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckIcon className="h-4 w-4" aria-hidden />
              Aprovar solicitação
            </button>
            <button
              type="button"
              onClick={onRequestAdjustments}
              className="erp-btn-secondary flex flex-1 items-center justify-center gap-2"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden />
              Solicitar ajustes
            </button>
          </div>
        ) : undefined
      }
    >
      <div className="border-b border-secondary-200 px-2">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {(
            [
              ['details', 'Detalhes'],
              ['comments', `Comentários (${item.comments.length})`],
              ['attachments', `Anexos (${item.attachments.length})`],
              ['history', 'Histórico'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => onTabChange(id)}
              className={`shrink-0 rounded-none border-b-2 px-3 py-2.5 text-sm font-medium ${
                activeTab === id
                  ? 'border-primary-500 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTab === 'details' ? (
          <div className="space-y-4">
            {adjustmentSent ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Ajustes solicitados. A equipe Baruk foi notificada.
              </p>
            ) : null}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Meta label="Tipo">
                <Tag className={typeClass[item.type]}>{typeLabel[item.type]}</Tag>
              </Meta>
              <Meta label="Prioridade">
                <Tag className={priorityClass[item.priority]}>{priorityLabel[item.priority]}</Tag>
              </Meta>
              <Meta label="Projeto">
                <Link
                  to={`/portal/projects/${item.projectId}`}
                  className="font-semibold text-primary-700 hover:underline"
                >
                  {item.projectName}
                </Link>
              </Meta>
              <Meta label="Status">
                <Tag className={statusClass[item.status]}>{statusLabel[item.status]}</Tag>
              </Meta>
              {item.impactSchedule ? (
                <Meta label="Impacto no prazo">
                  <span className="font-semibold text-secondary-900">{item.impactSchedule}</span>
                </Meta>
              ) : null}
              {item.impactCost ? (
                <Meta label="Impacto no custo">
                  <span className="font-semibold text-secondary-900">{item.impactCost}</span>
                </Meta>
              ) : null}
              <Meta label="Criada em">
                <span className="font-semibold text-secondary-900">{item.createdAtLabel}</span>
              </Meta>
              <Meta label="Última atualização">
                <span className="font-semibold text-secondary-900">{item.updatedAtLabel}</span>
              </Meta>
              <Meta label="Solicitada por">
                <PersonCell name={item.requestedByName} role={item.requestedByRole} />
              </Meta>
              <Meta label="Responsável">
                <PersonCell name={item.ownerName} role={item.ownerRole} />
              </Meta>
            </div>

            <section>
              <p className="text-xs font-medium text-secondary-500">Descrição completa</p>
              <p className="mt-1 text-sm leading-relaxed text-secondary-700">{item.description}</p>
            </section>

            <section>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-secondary-900">Anexos ({item.attachments.length})</p>
                {item.attachments.length > 0 ? (
                  <button type="button" className="text-xs font-semibold text-primary-700 hover:underline">
                    Ver todos
                  </button>
                ) : null}
              </div>
              <ul className="mt-2 space-y-2">
                {item.attachments.length === 0 ? (
                  <li className="text-sm text-secondary-500">Nenhum anexo.</li>
                ) : (
                  item.attachments.map((file) => (
                    <li
                      key={file.id}
                      className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2.5"
                    >
                      <DocumentTextIcon className="h-5 w-5 shrink-0 text-secondary-500" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-secondary-900">{file.name}</p>
                        <p className="text-xs text-secondary-500">
                          {file.sizeLabel} · {file.dateLabel}
                        </p>
                      </div>
                      <a
                        href={file.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg p-1.5 text-secondary-500 hover:bg-white hover:text-primary-700"
                        aria-label={`Baixar ${file.name}`}
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                      </a>
                    </li>
                  ))
                )}
              </ul>
            </section>

            <section>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-secondary-900">Vínculos</p>
                <button type="button" className="text-xs font-semibold text-primary-700 hover:underline">
                  + Vincular
                </button>
              </div>
              <ul className="mt-2 space-y-2">
                {item.links.length === 0 ? (
                  <li className="text-sm text-secondary-500">Nenhum vínculo.</li>
                ) : (
                  item.links.map((link) => (
                    <li
                      key={link.id}
                      className="flex items-center gap-2 rounded-xl border border-secondary-200 px-3 py-2 text-sm text-secondary-700"
                    >
                      <LinkIcon className="h-4 w-4 text-secondary-400" aria-hidden />
                      <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                        {link.kindLabel}
                      </span>
                      <span className="font-medium text-secondary-900">{link.title}</span>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </div>
        ) : null}

        {activeTab === 'comments' ? (
          <ul className="space-y-4">
            {item.comments.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum comentário.</li>
            ) : (
              item.comments.map((comment) => (
                <li key={comment.id} className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                    {initials(comment.authorName)}
                  </span>
                  <div>
                    <p className="text-sm text-secondary-800">
                      <span className="font-semibold">{comment.authorName}</span>
                      <span className="text-secondary-400"> · {comment.whenLabel}</span>
                    </p>
                    <p className="mt-1 text-sm text-secondary-700">{comment.body}</p>
                  </div>
                </li>
              ))
            )}
          </ul>
        ) : null}

        {activeTab === 'attachments' ? (
          <ul className="space-y-2">
            {item.attachments.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum anexo.</li>
            ) : (
              item.attachments.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center gap-3 rounded-xl border border-secondary-200 px-3 py-2.5"
                >
                  <DocumentTextIcon className="h-5 w-5 text-secondary-500" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-secondary-900">{file.name}</p>
                    <p className="text-xs text-secondary-500">
                      {file.sizeLabel} · {file.dateLabel}
                    </p>
                  </div>
                  <a
                    href={file.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg p-1.5 text-secondary-500 hover:bg-secondary-50 hover:text-primary-700"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                  </a>
                </li>
              ))
            )}
          </ul>
        ) : null}

        {activeTab === 'history' ? (
          <ul className="space-y-3">
            {item.history.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum histórico.</li>
            ) : (
              item.history.map((entry) => (
                <li key={entry.id} className="rounded-xl border border-secondary-200 px-3 py-2.5">
                  <p className="text-sm font-semibold text-secondary-900">{entry.title}</p>
                  <p className="mt-0.5 text-sm text-secondary-600">{entry.detail}</p>
                  <p className="mt-1 text-xs text-secondary-400">{entry.whenLabel}</p>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </DraggableDetailPanel>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-secondary-500">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function PersonCell({ name, role }: { name: string; role: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
        {initials(name)}
      </span>
      <span>
        <span className="block text-sm font-semibold text-secondary-900">{name}</span>
        <span className="block text-xs text-secondary-500">{role}</span>
      </span>
    </span>
  );
}
